import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import {
  Stamp as StampIcon, FileText, Clock, ShieldCheck, ExternalLink, Info, CheckCircle2, AlertCircle, Globe2,
} from 'lucide-react';

function ProvenanceBadge({ result }) {
  if (result.is_live) return <Badge className="rounded-full bg-accent/15 text-accent border-accent/30" data-testid="visa-provenance">Provider-sourced · Live</Badge>;
  return <Badge variant="outline" className="rounded-full bg-amber-500/12 text-amber-700 border-amber-500/30" data-testid="visa-provenance">Indicative (demo) — verify officially</Badge>;
}

export default function Visa() {
  const [meta, setMeta] = useState(null);
  const [passport, setPassport] = useState('IN');
  const [destination, setDestination] = useState('TH');
  const [purpose, setPurpose] = useState('tourism');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'Visa & entry requirements | FlightDeals.in';
    api.visaMeta().then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const check = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.visaCheck({ passport, destination, purpose, date: date || null });
      setResult(data);
    } catch (e) { setResult({ error: 'failed', message: 'Could not fetch requirements. Try again.' }); }
    finally { setLoading(false); }
  };

  const openCta = () => {
    // Strict central attribution: NEVER open a partner URL directly. If no tracked offer
    // exists yet, do nothing (the backend always mints a cta_offer_id when a CTA is available).
    if (!result?.cta_offer_id) return;
    api.visaTrack('visa_cta_click', { destination, passport });
    window.open(api.goVerticalUrl('visa', result.cta_offer_id, { surface: 'visa_result', source: '/visa' }), '_blank');
  };

  const countries = meta?.countries || [];
  const req = result?.visa_required;

  return (
    <Layout>
      <section className="relative bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-3"><Globe2 className="h-3.5 w-3.5 text-accent" /> New · Visa vertical</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-2"><StampIcon className="h-8 w-8 text-accent" /> Visa & entry requirements</h1>
          <p className="text-primary-foreground/80 mt-1 max-w-2xl">Check indicative entry requirements for your trip. We surface provider-sourced guidance — always confirm officially before you travel.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Card className="p-4 sm:p-5 rounded-2xl card-elevate" data-testid="visa-form">
          <div className="grid gap-3 md:grid-cols-5 items-end">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Passport / Citizenship</label>
              <Select value={passport} onValueChange={setPassport}>
                <SelectTrigger className="mt-1" data-testid="visa-passport"><SelectValue /></SelectTrigger>
                <SelectContent>{countries.map((c) => <SelectItem key={c.country_code} value={c.country_code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Destination</label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger className="mt-1" data-testid="visa-destination"><SelectValue /></SelectTrigger>
                <SelectContent>{countries.map((c) => <SelectItem key={c.country_code} value={c.country_code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Purpose</label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger className="mt-1" data-testid="visa-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.purposes || ['tourism']).map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Travel date (optional)</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="visa-date" />
            </div>
            <Button onClick={check} disabled={loading || passport === destination} className="h-10 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="visa-check-button">
              {loading ? 'Checking…' : 'Check requirements'}
            </Button>
          </div>
          {passport === destination && <p className="text-xs text-destructive mt-2">Passport and destination cannot be the same.</p>}
        </Card>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && <Card className="p-6 rounded-2xl"><Skeleton className="h-6 w-40 mb-4" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-2/3" /></Card>}
        {result && result.error && (
          <Card className="p-6 rounded-2xl text-center" data-testid="visa-error"><AlertCircle className="h-8 w-8 mx-auto text-amber-500 mb-2" /><p className="text-muted-foreground">{result.message}</p></Card>
        )}
        {result && !result.error && (
          <Card className="p-6 rounded-2xl" data-testid="visa-result">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <span className={`grid place-items-center h-12 w-12 rounded-xl ${req ? 'bg-amber-500/12 text-amber-600' : req === false ? 'bg-emerald-500/12 text-emerald-600' : 'bg-secondary text-muted-foreground'}`}>
                  {req === false ? <CheckCircle2 className="h-6 w-6" /> : <StampIcon className="h-6 w-6" />}
                </span>
                <div>
                  <p className="font-display text-xl font-semibold" data-testid="visa-verdict">
                    {req === true ? 'Visa required' : req === false ? 'No visa required' : 'Requirements vary'}
                  </p>
                  <p className="text-sm text-muted-foreground">{result.summary}</p>
                </div>
              </div>
              <ProvenanceBadge result={result} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[['Visa type', result.visa_type], ['Entry type', result.entry_type], ['Stay duration', result.stay_duration],
                ['Validity', result.validity], ['Processing', result.processing], ['Fees', result.fees]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-border p-3">
                  <p className="text-xs text-muted-foreground">{k}</p>
                  <p className="font-medium text-sm mt-0.5">{v || '—'}</p>
                </div>
              ))}
            </div>

            {result.documents?.length > 0 && (
              <div className="mt-5">
                <p className="font-semibold text-sm flex items-center gap-1.5 mb-2"><FileText className="h-4 w-4 text-accent" /> Documents typically needed</p>
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {result.documents.map((d) => <li key={d} className="text-sm text-muted-foreground flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-accent shrink-0" /> {d}</li>)}
                </ul>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button onClick={openCta} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="visa-cta-button">
                {result.cta_label || 'Check official requirements'} <ExternalLink className="h-4 w-4 ml-1.5" />
              </Button>
              <p className="text-[11px] text-muted-foreground">Opens partner / official resource</p>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-secondary/50 p-3 text-xs text-muted-foreground flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-accent" /> {result.disclaimer}
              {result.sources?.length > 0 && <span className="ml-1">Source: {result.sources.join(', ')}</span>}
            </div>
          </Card>
        )}

        {!result && !loading && (
          <div className="grid gap-4 md:grid-cols-3">
            {[[ShieldCheck, 'Plan with confidence', 'Know visa type, stay length and documents before you book.'],
              [Clock, 'Save time', 'One check instead of hunting across embassy sites.'],
              [Info, 'Always verify', 'We show indicative guidance — the embassy is the final word.']].map(([Ic, t, s]) => (
              <Card key={t} className="p-5 rounded-2xl"><span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/10 text-accent mb-3"><Ic className="h-5 w-5" /></span><p className="font-semibold">{t}</p><p className="text-sm text-muted-foreground mt-1">{s}</p></Card>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
